import { apiClient } from './client';

/**
 * Platform-wide reference values for the participant performance
 * grid's Faculty / experienced-cohort comparison bars (ROADMAP #44).
 *
 * Seeded by alembic 0033 from the IRCAD mastery study. Future
 * in-system aggregates land as additional rows with a different
 * `source` tag.
 */
export interface MasterBaseline {
  id: string;
  /** How the procedure is performed: 'manual' (a person) | 'robotic'.
   *  Part of the unique lookup key alongside cohort/group/metric/statistic. */
  operator: string;
  /** 'attending' | 'practice_ready' | 'other' | 'not_practice_ready'. */
  cohort: string;
  /** Null = not procedure-specific (e.g. EEG cohort baseline). */
  procedure_group: string | null;
  /** 'focus' | 'engagement' | 'bimanual_dexterity_v1' | … */
  metric: string;
  statistic: 'mean' | 'median' | 'min' | 'max';
  /** Null when catalogued but the cohort value wasn't established. */
  value: number | null;
  /** Parsed p-value (null when the source gave only "NS"). */
  p_value: number | null;
  /** Whether the metric is a usable discriminator — comparisons + the
   *  mastery rate only fire on significant rows that carry a value. */
  significant: boolean;
  /** Which way is "better": 'higher' | 'lower' | 'none'. */
  direction: 'higher' | 'lower' | 'none';
  /** Which sensor the metric comes from: 'hand' | 'eeg' | 'both'. */
  modality: 'hand' | 'eeg' | 'both';
  /** Display hint: 's' | 'm' | 'm_per_s' | 'm3' | 'fraction' |
   *  'unitless'. */
  unit: string | null;
  sample_size: number | null;
  source: string | null;
  created_at: string;
}

export interface MasterBaselineGroups {
  groups: string[];
}

/** A baseline procedure in the catalogue — what a tenant procedure
 *  maps TO. */
export interface MasterBaselineProcedure {
  id: string;
  key: string;
  display_name: string;
  modality: 'hand' | 'eeg' | 'both';
}

/** A tenant procedure + its effective baseline mapping. */
export interface BaselineMapEntry {
  procedure_id: string;
  procedure_number: number;
  name: string;
  baseline_group: string | null;
  source: 'explicit' | 'legacy' | 'none';
}

export async function listMasterBaselineProcedures(): Promise<
  MasterBaselineProcedure[]
> {
  const { data } = await apiClient.get<MasterBaselineProcedure[]>(
    '/api/v1/master-baseline-procedures',
  );
  return data;
}

export async function getBaselineMap(
  tenantId: string,
): Promise<BaselineMapEntry[]> {
  const { data } = await apiClient.get<BaselineMapEntry[]>(
    `/api/v1/tenants/${tenantId}/baseline-map`,
  );
  return data;
}

/** Set (group) or clear (null) a procedure's explicit baseline mapping. */
export async function setBaselineMap(
  tenantId: string,
  procedureId: string,
  baselineGroup: string | null,
): Promise<BaselineMapEntry> {
  const { data } = await apiClient.put<BaselineMapEntry>(
    `/api/v1/tenants/${tenantId}/procedures/${procedureId}/baseline-map`,
    { baseline_group: baselineGroup },
  );
  return data;
}

export async function listMasterBaselines(): Promise<MasterBaseline[]> {
  const { data } = await apiClient.get<MasterBaseline[]>(
    '/api/v1/master-baselines',
  );
  return data;
}

export async function listMasterBaselineGroups(): Promise<MasterBaselineGroups> {
  const { data } = await apiClient.get<MasterBaselineGroups>(
    '/api/v1/master-baselines/groups',
  );
  return data;
}

// ---------------------------------------------------------------------
// Superadmin CRUD
// ---------------------------------------------------------------------

export interface MasterBaselineCreate {
  /** 'manual' | 'robotic'. Omit to default to 'manual'. */
  operator?: string;
  cohort: string;
  procedure_group?: string | null;
  metric: string;
  statistic: 'mean' | 'median' | 'min' | 'max';
  value?: number | null;
  p_value?: number | null;
  significant?: boolean;
  direction?: 'higher' | 'lower' | 'none';
  modality?: 'hand' | 'eeg' | 'both';
  unit?: string | null;
  sample_size?: number | null;
  source?: string | null;
}

export interface MasterBaselineUpdate {
  value?: number | null;
  p_value?: number | null;
  significant?: boolean;
  direction?: 'higher' | 'lower' | 'none';
  modality?: 'hand' | 'eeg' | 'both';
  unit?: string | null;
  sample_size?: number | null;
  source?: string | null;
}

export async function createMasterBaseline(
  body: MasterBaselineCreate,
): Promise<MasterBaseline> {
  const { data } = await apiClient.post<MasterBaseline>(
    '/api/v1/master-baselines',
    body,
  );
  return data;
}

export async function updateMasterBaseline(
  id: string,
  body: MasterBaselineUpdate,
): Promise<MasterBaseline> {
  const { data } = await apiClient.patch<MasterBaseline>(
    `/api/v1/master-baselines/${id}`,
    body,
  );
  return data;
}

export async function deleteMasterBaseline(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/master-baselines/${id}`);
}

export interface GroupOpResult {
  rows_touched: number;
}

/** Rename a procedure_group everywhere — catalogue + every tenant's
 *  procedures.procedure_group. One transaction across schemas. */
export async function renameMasterBaselineGroup(
  oldName: string,
  newName: string,
): Promise<GroupOpResult> {
  const { data } = await apiClient.patch<GroupOpResult>(
    `/api/v1/master-baselines/groups/${encodeURIComponent(oldName)}`,
    { new_name: newName },
  );
  return data;
}

/** Delete every catalogue row in this group AND null procedure_group
 *  on every tenant procedure referencing it. */
export async function deleteMasterBaselineGroup(
  name: string,
): Promise<GroupOpResult> {
  const { data } = await apiClient.delete<GroupOpResult>(
    `/api/v1/master-baselines/groups/${encodeURIComponent(name)}`,
  );
  return data;
}

// ---------------------------------------------------------------------
// CSV import (superadmin) — dry-run preview then commit
// ---------------------------------------------------------------------

export interface ImportPlannedRow {
  cohort: string;
  procedure_group: string | null;
  metric: string;
  statistic: string;
  value: number | null;
  p_value: number | null;
  significant: boolean;
  direction: string;
  modality: string;
  unit: string | null;
  sample_size: number | null;
  note: string;
}

export interface BaselineImportPreview {
  rows: ImportPlannedRow[];
  new_procedures: { key: string; display_name: string }[];
  warnings: string[];
  row_count: number;
}

export interface BaselineImportResult {
  created: number;
  updated: number;
  procedures_created: number;
}

export async function previewBaselineImport(
  csv: string,
  source?: string,
): Promise<BaselineImportPreview> {
  const { data } = await apiClient.post<BaselineImportPreview>(
    '/api/v1/master-baselines/import/preview',
    { csv, ...(source ? { source } : {}) },
  );
  return data;
}

export async function commitBaselineImport(
  csv: string,
  source?: string,
): Promise<BaselineImportResult> {
  const { data } = await apiClient.post<BaselineImportResult>(
    '/api/v1/master-baselines/import',
    { csv, ...(source ? { source } : {}) },
  );
  return data;
}
